import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { PLACEHOLDERS } from '../../constants/mail-template-placeholders'
import { useAivyContext, usePartnerContext } from '../../context'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'
import MailTemplatePreview from './mail-template-preview'

const I18N = 'mail_template.preview_dialog.'

// Feste Absenderadresse von inviteTalent (create-email.js), davor steht der
// Name der Firma
export const SENDER_ADDRESS = 'notification@aivy.app'

const NAME_FIELDS = [
  { field: 'firstname', token: PLACEHOLDERS.FIRSTNAME.token },
  { field: 'lastname', token: PLACEHOLDERS.LASTNAME.token }
]

// Setzt Vor- und Nachname des Talents statt der Beispielnamen ein, soweit
// sie schon eingetragen sind
const personalizeMacros = (macros, recipient) =>
  macros.map((macro) => {
    const name = NAME_FIELDS.find(({ token }) => token === macro.macro)
    const value = name && recipient?.[name.field]?.trim()

    return value ? { ...macro, value } : macro
  })

// Adressen kürzen ab, der Betreff bricht um wie im Mailprogramm
const EnvelopeRow = ({ label, children, wrap }) => (
  <div className='flex min-w-0 gap-x-3'>
    <dt className='w-16 shrink-0 text-gray-500'>{label}</dt>
    <dd
      className={`min-w-0 flex-1 text-gray-900 ${
        wrap ? 'break-words' : 'truncate'
      }`}
    >
      {children}
    </dd>
  </div>
)

EnvelopeRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  wrap: PropTypes.bool
}

EnvelopeRow.defaultProps = {
  wrap: false
}

// Vorschau einer Mail-Vorlage wie im Postfach. Oben stehen Absender,
// Empfänger und Betreff, darunter die Mail. Kennt der Aufrufer das Talent
// schon, stehen dessen Adresse und Name drin, sonst Beispielwerte. Kopf und
// Fuß bleiben stehen, nur die Mail scrollt. Links in der Mail führen nirgends hin.
const MailPreviewDialog = ({
  open,
  onClose,
  mailTemplate,
  data,
  mailTemplateMacros,
  recipient,
  onEdit,
  footerStart
}) => {
  const { t } = useTranslation()
  const { system } = useAivyContext()
  const { partner } = usePartnerContext()

  const body = JSON.stringify(data)
  const usesSampleName = NAME_FIELDS.some(
    ({ field, token }) => body.includes(token) && !recipient?.[field]?.trim()
  )

  const senderName = partner?.display_name || partner?.name

  let to = (
    <span className='italic text-gray-400'>{t(I18N + 'to_missing')}</span>
  )
  if (recipient?.email) {
    to = recipient.more
      ? t(I18N + 'to_more', { email: recipient.email, count: recipient.more })
      : recipient.email
  }

  return (
    <Modal open={open} setOpen={onClose} size='3xl' zIndex='z-50' padding='p-0'>
      <div className='flex max-h-[85vh] flex-col'>
        <div className='px-6 pb-4 pt-6 sm:px-8'>
          <h2 className='pr-10 text-xl font-bold text-gray-900'>
            {t(I18N + 'title')}
          </h2>
        </div>
        <div className='border-y border-gray-200 px-6 py-3 sm:px-8'>
          <dl className='space-y-1 text-sm'>
            <EnvelopeRow label={t(I18N + 'from_label')}>
              {senderName}{' '}
              <span className='text-gray-500'>&lt;{SENDER_ADDRESS}&gt;</span>
            </EnvelopeRow>
            <EnvelopeRow label={t(I18N + 'to_label')}>{to}</EnvelopeRow>
            <EnvelopeRow label={t(I18N + 'subject_label')} wrap>
              <span className='font-semibold'>{mailTemplate.subject}</span>
            </EnvelopeRow>
          </dl>
          <p className='mt-2 text-xs text-gray-500'>
            {t(I18N + (usesSampleName ? 'sample_hint_name' : 'sample_hint'))}
          </p>
        </div>
        <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain'>
          <MailTemplatePreview
            system={system}
            imageUrl={mailTemplate.imageUrl}
            language={mailTemplate.language}
            useInvitationBlock={mailTemplate.useInvitationBlock}
            data={data}
            mailTemplateMacros={personalizeMacros(
              mailTemplateMacros,
              recipient
            )}
          />
        </div>
        <div className='flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-6 py-4 sm:px-8'>
          <div>{footerStart}</div>
          <div className='flex gap-x-2'>
            {onEdit && (
              <Button.SecondaryLG
                text={t(I18N + 'edit_action')}
                onClick={onEdit}
              />
            )}
            <Button.PrimaryLG
              autoFocus
              text={t(I18N + 'close_action')}
              onClick={onClose}
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}

MailPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mailTemplate: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  mailTemplateMacros: PropTypes.array.isRequired,
  // Adresse und Name, soweit bekannt. more zählt weitere Adressen.
  recipient: PropTypes.shape({
    email: PropTypes.string,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    more: PropTypes.number
  }),
  onEdit: PropTypes.func,
  footerStart: PropTypes.node
}

MailPreviewDialog.defaultProps = {
  recipient: undefined,
  onEdit: undefined,
  footerStart: null
}

export default MailPreviewDialog
